/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, X, Search } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import { vendorOrganisationServices } from '@/Services/VendorOrganisationServices/vendorOrganisation.services';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';
import { BaseModal } from '@/Components/Modals/BaseModal';
import { AlertModal } from '@/Components/Modals';
import { notificationServices } from '@/Services';
import { modalActions } from '@/Actions';
import { useDispatch } from 'react-redux';
import { CheckPicker } from 'rsuite';
import allVendorServices from '@/Services/Form Services/allvendor.services';

const columns = [
    {
        title: 'ID',
        key: 'id',
        width: 60,
    },
    {
        title: 'NAME',
        key: 'name',
        width: 200,
    },
    {
        title: 'CLIENT CODE',
        key: 'organisationClientCode',
        width: 150,
    },
    // {
    //     title: 'DESCRIPTION',
    //     key: 'description',
    //     width: 300,
    // },
    // {
    //     title: 'STATUS',
    //     key: 'status',
    //     width: 100,
    // },
    // {
    //     title: 'VENDORS',
    //     key: 'vendors',
    //     width: 150,
    // },
    {
        title: 'ACTIONS',
        key: 'actions',
        width: 120,
    },
];

const EMPTY_FORM = { id: null, name: '', organisationClientCode: '', description: '', active: true };

// ── Vendor List Modal Sub-component ──────────────────────────────────────────
const VendorListModal = ({ org, onClose }) => {
    const queryClient = useQueryClient();
    const [selectedIds, setSelectedIds] = useState([]);
    const [vendorSearch, setVendorSearch] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Fetch all available vendors
    const { data: vendorsData } = useQuery(
        ['allAvailableVendors'],
        () => allVendorServices.listVendor({ req: { params: {} }, type: 'VENDOR' }),
        { staleTime: 300000 }
    );

    // Fetch current mappings
    const { data: mappingsData, isLoading: mappingsLoading } = useQuery(
        ['vendorMappings', org?.id],
        () => vendorOrganisationServices.listVendorMappings({ data: { organisationId: org.id } }),
        {
            enabled: !!org?.id,
        }
    );

    // Sync selection with server data whenever it changes or modal opens
    useEffect(() => {
        if (org?.id && mappingsData?.data) {
            const currentMappedIds = mappingsData.data.map(m => Number(m.vendorId));
            setSelectedIds(currentMappedIds);
        }
    }, [mappingsData?.data, org?.id]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await vendorOrganisationServices.mapOrganisationVendors({
                data: {
                    organisationId: Number(org?.id),
                    vendorIds: selectedIds.map(Number)
                }
            });

            await queryClient.invalidateQueries(['vendorMappings', org?.id]);

            notificationServices.generateNotification({
                type: 'success',
                message: 'Vendor mappings updated successfully',
            });
            onClose();
        } catch (err) {
            notificationServices.generateNotification({
                type: 'error',
                message: err?.message ?? 'Failed to update mappings. Please check console.',
            });
            console.error('Mapping update failed:', err);
        } finally {
            setIsSaving(false);
        }
    };

    const vendors = vendorsData?.data ?? [];
    const picklistData = vendors.map(v => ({ label: v.name, value: v.id }));

    // For the chips display, we need to find names of selected vendors
    const selectedVendors = vendors.filter(v => selectedIds.includes(v.id));
    const filteredMappings = selectedVendors.filter(v =>
        v.name?.toLowerCase()?.includes(vendorSearch.toLowerCase())
    );

    return (
        <BaseModal
            open={!!org}
            handleClose={onClose}
            heading={`Mapped Vendors: ${org?.name ?? ''}`}
            width="42rem"
            height="auto"
            renderBtns={() => (
                <div className="flex items-center justify-end gap-3 w-full px-6 pb-4">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onClose}
                        className="min-w-[100px]"
                    >
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold min-w-[100px] shadow-sm"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            )}
        >
            <div className="flex flex-col h-full gap-4 p-5">
                {/* Management UI */}
                <div className="w-full flex flex-col gap-2 mb-2 bg-gray-50/50 p-4 rounded-xl border border-gray-100">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                        Select Vendors
                    </label>
                    <CheckPicker
                        data={picklistData}
                        value={selectedIds}
                        onChange={setSelectedIds}
                        placeholder="Choose vendors to add..."
                        style={{ width: '100%' }}
                        menuStyle={{ zIndex: 9999 }}
                        virtualized
                        searchable
                        cleanable={false}
                    />
                    <p className="text-[10px] text-gray-400 mt-1 ml-1">
                        Select vendors from the dropdown. Changes will stay pending until you click Save.
                    </p>
                </div>

                <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                            Current Selection ({selectedIds.length})
                        </label>
                        {/* Search vendors within current selection */}
                        <div className="relative w-48">
                            <input
                                type="text"
                                placeholder="Filter selection..."
                                className="w-full pl-8 pr-3 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-yellow-400"
                                value={vendorSearch}
                                onChange={(e) => setVendorSearch(e.target.value)}
                            />
                            <Search size={12} className="absolute left-2.5 top-2 text-gray-400" />
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 max-h-[16rem] overflow-y-auto pr-2 custom-scrollbar min-h-[4rem]">
                        {mappingsLoading ? (
                            <div className="flex items-center gap-2 text-sm text-gray-400 italic py-4">
                                <span className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></span>
                                Loading current mappings...
                            </div>
                        ) : filteredMappings.length === 0 ? (
                            <p className="text-sm text-gray-400 italic py-4">
                                {selectedIds.length === 0
                                    ? 'No vendors selected yet.'
                                    : 'No matching vendors in selection.'}
                            </p>
                        ) : (
                            filteredMappings.map((v) => (
                                <div
                                    key={v.id}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-white text-gray-700 border border-gray-200 shadow-sm hover:border-red-200 hover:bg-red-50 transition-colors group"
                                >
                                    {v.name}
                                    <X
                                        size={12}
                                        className="text-gray-400 group-hover:text-red-500 cursor-pointer"
                                        onClick={() => setSelectedIds(prev => prev.filter(id => id !== v.id))}
                                    />
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </BaseModal>
    );
};


function OrganisationListPage() {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [viewVendorsOrg, setViewVendorsOrg] = useState(null); // Org for which we are viewing vendors
    const [form, setForm] = useState(EMPTY_FORM);
    const [formError, setFormError] = useState('');
    const [vendorSearch, setVendorSearch] = useState('');

    // ── Data fetching ──────────────────────────────────────────────────────────
    const { data: orgsData, isLoading, refetch } = useQuery(
        ['organisations'],
        () => vendorOrganisationServices.listOrganisations({ data: {} }),
        {
            retry: false,
            onError: () => {
                notificationServices.generateNotification({
                    type: 'error',
                    message: 'Failed to load organisations',
                });
            },
        }
    );

    const organisations = orgsData?.data ?? [];

    // ── Mutations ──────────────────────────────────────────────────────────────
    const createEditMutation = useMutation(
        (payload) => vendorOrganisationServices.createEditOrganisation({ data: payload }),
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(['organisations']);
                refetch();
                notificationServices.generateNotification({
                    type: 'success',
                    message: form.id ? 'Organisation updated' : 'Organisation created',
                });
                setModalOpen(false);
                setForm(EMPTY_FORM);
                setFormError('');
            },
            onError: (err) => {
                notificationServices.generateNotification({
                    type: 'error',
                    message: err?.message ?? 'Failed to save organisation',
                });
            },
        }
    );

    const deleteMutation = useMutation(
        (id) => vendorOrganisationServices.deleteOrganisation({ data: { id } }),
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(['organisations']);
                refetch();
                notificationServices.generateNotification({
                    type: 'success',
                    message: 'Organisation deleted',
                });
            },
            onError: (err) => {
                notificationServices.generateNotification({
                    type: 'error',
                    message: err?.message ?? 'Failed to delete organisation',
                });
            },
        }
    );

    // ── Handlers ───────────────────────────────────────────────────────────────
    const handleOpenCreate = () => {
        setForm(EMPTY_FORM);
        setFormError('');
        setModalOpen(true);
    };

    const handleOpenEdit = (org) => {
        setForm({
            id: org.id,
            name: org.name ?? '',
            organisationClientCode: org.organisationClientCode ?? '',
            description: org.description ?? '',
            active: org.active ?? true
        });
        setFormError('');
        setModalOpen(true);
    };

    const handleSave = () => {
        if (!form.name.trim()) {
            setFormError('Name is required');
            return;
        }
        const payload = {
            name: form.name.trim(),
            organisationClientCode: form.organisationClientCode.trim(),
            description: form.description.trim(),
            active: form.active
        };
        if (form.id) payload.id = form.id;
        createEditMutation.mutate(payload);
    };

    const handleDeleteConfirm = (org) => {
        dispatch(
            modalActions.toggleAlert(true, {
                heading: 'Delete Organisation',
                message: `Are you sure you want to delete "${org.name}"? This action cannot be undone.`,
                handleConfirm: () => {
                    deleteMutation.mutate(org.id);
                    dispatch(modalActions.toggleAlert(false));
                },
                confirmText: 'Delete',
                cancelText: 'Cancel',
                colorReverse: true,
            })
        );
    };

    // ── Table row renderer ─────────────────────────────────────────────────────
    const renderCol = (col, item) => {
        if (col.key === 'status') {
            return item.active ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    Active
                </span>
            ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                    Inactive
                </span>
            );
        }
        if (col.key === 'vendors') {
            return (
                <Button
                    variant="link"
                    size="sm"
                    className="text-xs h-7 px-0 text-[#f7b500] hover:text-[#d99f00] font-semibold"
                    onClick={() => setViewVendorsOrg(item)}
                >
                    View Vendors
                </Button>
            );
        }
        if (col.key === 'actions') {
            return (
                <div className="flex items-center gap-2">
                    <Button
                        id={`edit-org-${item.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenEdit(item)}
                        className="h-7 px-2"
                    >
                        <Pencil size={13} />
                    </Button>
                    <Button
                        id={`delete-org-${item.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteConfirm(item)}
                        className="h-7 px-2 text-red-500 hover:text-red-600 hover:border-red-300"
                    >
                        <Trash2 size={13} />
                    </Button>
                </div>
            );
        }
        return null;
    };


    const mappingList = []; // Removed global dependency to avoid sync issues during modal refactor

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className="flex flex-col gap-4 p-6">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-bold text-gray-800">Vendor Organisations</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Manage organisations and their vendor mappings</p>
                </div>
                <Button
                    id="add-organisation-btn"
                    onClick={handleOpenCreate}
                    className="flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold"
                >
                    <Plus size={16} />
                    Add Organisation
                </Button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                {isLoading ? (
                    <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
                        Loading organisations...
                    </div>
                ) : organisations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                        <p className="text-sm">No organisations found.</p>
                        <p className="text-xs mt-1">Click "Add Organisation" to create the first one.</p>
                    </div>
                ) : (
                    <VariantTable
                        columns={columns}
                        data={organisations}
                        customRowData={{
                            actions: (item) => renderCol(columns.find(c => c.key === 'actions'), item),
                        }}
                    />
                )}
            </div>

            {/* Create / Edit Modal */}
            <BaseModal
                open={modalOpen}
                handleClose={() => { setModalOpen(false); setFormError(''); }}
                heading={form.id ? 'Edit Organisation' : 'Add Organisation'}
                width="35rem"
                height="auto"
                customStyles={{ minHeight: '20rem' }}
                renderBtns={() => (
                    <div className="flex items-center justify-end gap-3 w-full px-6 pb-4">
                        <Button
                            id="org-modal-cancel"
                            variant="outline"
                            size="sm"
                            onClick={() => { setModalOpen(false); setFormError(''); }}
                            className="min-w-[80px]"
                        >
                            Cancel
                        </Button>
                        <Button
                            id="org-modal-save"
                            size="sm"
                            className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold min-w-[80px] shadow-sm"
                            onClick={handleSave}
                            disabled={createEditMutation.isLoading}
                        >
                            {createEditMutation.isLoading ? 'Saving...' : form.id ? 'Update' : 'Create'}
                        </Button>
                    </div>
                )}
            >
                <div className="flex flex-col gap-6 p-4">
                    {/* Name & Client Code Group */}
                    <div className="grid grid-cols-2 gap-4">
                        {/* Name */}
                        <div className="flex flex-col gap-2">
                            <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider ml-1">
                                Organisation Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="org-name-input"
                                type="text"
                                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400 transition-all placeholder:text-gray-300"
                                value={form.name}
                                onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); setFormError(''); }}
                                placeholder="e.g. Acme Corporation"
                            />
                            {formError && <p className="text-[11px] text-red-500 ml-1 font-medium">{formError}</p>}
                        </div>

                        {/* Client Code */}
                        <div className="flex flex-col gap-2">
                            <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider ml-1">
                                Client Code
                            </label>
                            <input
                                id="org-client-code-input"
                                type="text"
                                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400 transition-all placeholder:text-gray-300"
                                value={form.organisationClientCode}
                                onChange={(e) => setForm((f) => ({ ...f, organisationClientCode: e.target.value }))}
                                placeholder="e.g. ACME-01"
                            />
                        </div>
                    </div>

                    {/* Description */}
                    <div className="flex flex-col gap-2">
                        <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider ml-1">
                            Description
                        </label>
                        <textarea
                            id="org-description-input"
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400 transition-all resize-none placeholder:text-gray-300"
                            value={form.description}
                            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                            placeholder="Provide a brief description of this organisation..."
                            rows={3}
                        />
                    </div>

                    {/* Active toggle (edit only) */}
                    {form.id && (
                        <div className="flex items-center gap-3 px-1">
                            <input
                                id="org-active-toggle"
                                type="checkbox"
                                className="w-4 h-4 accent-yellow-400 cursor-pointer rounded border-gray-300"
                                checked={form.active}
                                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                            />
                            <label htmlFor="org-active-toggle" className="text-sm font-semibold text-gray-700 cursor-pointer">
                                Active Organisation
                            </label>
                        </div>
                    )}
                </div>
            </BaseModal>

            {/* Alert modal is mounted globally via Redux */}
            <AlertModal />

            {/* Vendor List Modal (Refactored Sub-component) */}
            {/* <VendorListModal 
                org={viewVendorsOrg} 
                onClose={() => setViewVendorsOrg(null)} 
            /> */}
        </div>
    );
}

export default OrganisationListPage;
